import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { avatarsApi } from "@/api/user/avatars";
import { User } from "lucide-react";
import { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

export const AvatarSettings = ({ onClose }) => {
  const { user, updateUserField } = useAuth();
  const { toast } = useToast();
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: avatars = [], isLoading } = useQuery({
    queryKey: ['avatars'],
    queryFn: avatarsApi.getAllAvatars
  });

  useEffect(() => {
    const fetchCurrentAvatar = async () => {
      if (user?.profile_picture) {
        try {
          const avatarData = await avatarsApi.getAvatarById(user.profile_picture);
          setCurrentAvatarUrl(avatarData.url);
        } catch (error) {
          // Handle error silently
        }
      }
    };

    fetchCurrentAvatar();
  }, [user?.profile_picture]);

  const handleSelectAvatar = async (avatarId: number) => {
    try {
      if (user) {
        await avatarsApi.updateProfilePicture(user.id, avatarId);
        await updateUserField(user.id, 'profile_picture', avatarId);

        const avatarData = await avatarsApi.getAvatarById(avatarId);
        setCurrentAvatarUrl(avatarData.url);

        queryClient.invalidateQueries({
          queryKey: ['user', user.id]
        });

        toast({
          title: "Success",
          description: "Avatar updated successfully",
        });

        if (onClose) {
          onClose();
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update avatar",
        variant: "destructive",
      });
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      if (user) {
        await avatarsApi.updateProfilePicture(user.id, null);
        await updateUserField(user.id, 'profile_picture', null);
        setCurrentAvatarUrl(null);

        // Invalidate and refetch queries that depend on user data
        queryClient.invalidateQueries({
          queryKey: ['user', user.id]
        });

        toast({
          title: "Success",
          description: "Avatar removed successfully",
        });

        // Close the dialog
        if (onClose) {
          onClose();
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to remove avatar",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading avatars...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Avatar className="h-20 w-20 shadow-lg border-2 border-[#9b87f5]/20 overflow-hidden bg-gradient-to-br from-[#F1F0FB] to-[#E5DEFF]">
          {currentAvatarUrl ? (
            <AvatarImage src={currentAvatarUrl} alt="Profile picture" className="object-cover" />
          ) : (
            <AvatarFallback className="bg-gradient-to-br from-[#9b87f5]/10 to-[#D946EF]/10">
              <User className="h-10 w-10 text-[#7E69AB]" />
            </AvatarFallback>
          )}
        </Avatar>
        {user?.profile_picture && (
          <Button
            variant="outline"
            onClick={handleRemoveAvatar}
            className="border-[#9b87f5]/20 text-[#7E69AB] hover:bg-[#9b87f5]/10 hover:text-[#7E69AB]"
          >
            Remove Avatar
          </Button>
        )}
      </div>

      <h4 className="text-md font-medium mt-4">Choose an avatar</h4>
      <ScrollArea className="h-[250px] pr-4">
        <div className="grid grid-cols-4 gap-4 mt-2">
          {avatars.map((avatar) => (
            <button
              key={avatar.id}
              onClick={() => handleSelectAvatar(avatar.id)}
              className={`relative rounded-lg overflow-hidden shadow-md hover:ring-2 hover:ring-primary hover:scale-105 transition-all duration-200 ${
                user?.profile_picture === avatar.id ? 'ring-2 ring-[#9b87f5]' : ''
              }`}
            >
              <img
                src={avatar.url}
                alt={`Avatar ${avatar.id}`}
                className="w-full h-full object-cover aspect-square"
              />
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
